import {
  SEMANTIC_ATTRIBUTE_SENTRY_OP,
  SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN,
  type BrowserOptions,
} from '@sentry/react';

type TracesSampler = NonNullable<BrowserOptions['tracesSampler']>;
type SamplingContext = Parameters<TracesSampler>[0];
type TraceKind = 'pageload' | 'navigation' | 'interaction' | 'other';

const DEV_SAMPLE_RATE = 1;
const DEFAULT_SAMPLE_RATE = 1 / 10;

const LIVE_TRACE_SAMPLE_RATES: Record<TraceKind, number> = {
  pageload: 0.05,
  navigation: 0.015,
  interaction: 0.03,
  other: 0.02,
};

const LIVE_REPLAY_SESSION_SAMPLE_RATE = 0.01;

const PAGELOAD_ORIGIN = 'auto.pageload.browser';
const NAVIGATION_ORIGIN = 'auto.navigation.browser';
const INTERACTION_OP_PREFIX = 'ui.interaction.';

const IGNORED_REQUEST_HOSTS = [
  'firebaseremoteconfig.googleapis.com',
  'firebaseremoteconfigrealtime.googleapis.com',
  'maps.googleapis.com',
  'clarity.ms',
  'google-analytics.com',
];

/** Resource span ops dropped from pageload/navigation transactions. */
export const IGNORED_RESOURCE_SPANS = ['resource.img', 'resource.image', 'resource.other'];

/** Error replays are always kept, in every environment. */
export const REPLAYS_ON_ERROR_SAMPLE_RATE = 1;

const getTraceKind = ({ attributes }: SamplingContext): TraceKind => {
  const origin = attributes?.[SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN];
  const op = attributes?.[SEMANTIC_ATTRIBUTE_SENTRY_OP];

  if (origin === PAGELOAD_ORIGIN) {
    return 'pageload';
  }
  if (origin === NAVIGATION_ORIGIN) {
    return 'navigation';
  }
  if (typeof op === 'string' && op.startsWith(INTERACTION_OP_PREFIX)) {
    return 'interaction';
  }
  return 'other';
};

const getTraceSampleRate = (appEnv: string | undefined, kind: TraceKind) => {
  if (appEnv === 'dev') {
    return DEV_SAMPLE_RATE;
  }
  if (appEnv === 'live') {
    return LIVE_TRACE_SAMPLE_RATES[kind];
  }
  return DEFAULT_SAMPLE_RATE;
};

/**
 * Builds the Sentry `tracesSampler` for an environment. Live samples per transaction kind.
 * Standalone interaction (INP) spans always use their own rate instead of inheriting the
 * route span's rate from the trace they belong to.
 */
export const createTracesSampler =
  (appEnv: string | undefined): TracesSampler =>
  (context) => {
    const kind = getTraceKind(context);
    const rate = getTraceSampleRate(appEnv, kind);
    return kind === 'interaction' ? rate : context.inheritOrSampleWith(rate);
  };

/** Session replay sample rate for an environment. */
export const getReplaysSessionSampleRate = (appEnv: string | undefined) => {
  if (appEnv === 'dev') {
    return DEV_SAMPLE_RATE;
  }
  if (appEnv === 'live') {
    return LIVE_REPLAY_SESSION_SAMPLE_RATE;
  }
  return DEFAULT_SAMPLE_RATE;
};

const getHostname = (url: string) => {
  try {
    return new URL(url, globalThis.location?.href).hostname;
  } catch {
    return '';
  }
};

/** `false` for third-party requests whose `http.client` spans only add noise. */
export const shouldCreateSpanForRequest = (url: string) => {
  const hostname = getHostname(url);
  return !IGNORED_REQUEST_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`));
};
